import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import rolesApiProvider from "../apiProvider/adminuserroleapi";
import { useLocation, useNavigate } from "react-router-dom";
import usePermission from "../hook/usePermission";
import "./styles/createRoleLayer.css";

export default function CreateRoleLayer({ isModal = false, editRoleProp = null, onClose = null }) {
  const [roleName, setRoleName] = useState("");
  const location = useLocation();
  const editRole = editRoleProp || location.state?.editRole;
  const [isEditMode, setIsEditMode] = useState(false);

  const canAdd = usePermission("userRolePermission", "add");
  const canEdit = usePermission("userRolePermission", "edit");

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const MODULE_KEYS = [
    // Dashboard
    "dashboard",

    // Website
    "homePage",

    // Catalog
    "categories",
    "attributes",
    "brands",
    "products",
    // "offerCreations",
    // "rootCreation",
    // "couponCreation",

    // Orders
    // "orderList",
    // "retailerOrderList",
    "customerOrder",
    // "posOrder",
    "returnRequest",
    "cancelledOrder",
    // "orderReport",

    // Users
    "usersList",
    "userRolePermission",
    "customerList",
    "contactRequests",
    // "passwordManagement",
    // "userActivityLog",
    // "usersReport",


    // POS
    // "posNew",
    // "posOrderHistroy",

    // Customer

    // "customerOrderHistroy",
    // "customerReports",

    // Wholesalers
    // "wholesalerList",
    // "wholesalerOrderHistory",
    // "wholesalerCreditManagement",
    // "wholesalerPaymentDues",
    // "wholesalerReports",

    // Retailers
    // "retailersList",
    // "retailerOrderHistory",
    // "retailerCreditManagement",
    // "retailerPaymentDues",
    // "retailerReports",

    // Vendors
    // "vendorList",
    // "vendorOrderList",
    // "vendorPayments",
    // "vendorPaymentDues",
    // "vendorReports",

    // Salesman
    // "salesmanList",
    // "salesmanTargetsIncentives",
    // "salesCashSettlement",
    // "salesmanPerformance",
    // "salesmanOrderHistory",
    // "salesmanReport",

    // Delivery
    // "deliveryList",
    // "deliveryTrackingUpdates",
    // "deliveryPersonList",
    // "deliveryPerformancePayroll",
    // "deliveryReports",
    // "deliveryManRequest",
    // "deliveryManComplaint",

    // Payment & Credit
    // "paymentTransactionList",
    // "recordManualPayment",
    // "outstandingPayment",
    // "processRefund",
    // "paymentCreditReport",

    // Inventory
    // "addStock",
    // "inventoryList",
    // "inventoryLog",
    // "inventoryReports",

    // Petty Cash
    // "pettycashTransactionList",
    // "pettyCashReport",

    // Tax
    "taxList",

    // Reports
    "productReport",
    "stockReport"
  ];

  const defaultPermissions = MODULE_KEYS.reduce((acc, key) => {
    acc[key] = { view: false, add: false, edit: false, delete: false };
    return acc;
  }, {});

  // State for rolePermissions
  const [rolePermissions, setRolePermissions] = useState(defaultPermissions);

  useEffect(() => {
    if (editRole) {
      setIsEditMode(true);
      console.log("editRole :", editRole);
      setRoleName(editRole.roleName || "");

      const mergedPermissions = { ...defaultPermissions };
      console.log("mergedPermissions (before):", mergedPermissions);

      const rolePermissionsObject = {};
      (editRole.rolePermissions || []).forEach((permission) => {
        rolePermissionsObject[permission.feature] = {
          view: permission.view,
          add: permission.add,
          edit: permission.edit,
          delete: permission.delete,
        };
      });

      Object.keys(rolePermissionsObject).forEach((feature) => {
        if (mergedPermissions[feature]) {
          mergedPermissions[feature] = {
            ...mergedPermissions[feature],
            ...rolePermissionsObject[feature],
          };
        }
      });

      console.log("mergedPermissions (after):", mergedPermissions);
      setRolePermissions(mergedPermissions);
    }
  }, [editRole]);

  const validateForm = () => {
    const newErrors = {};

    if (!roleName.trim()) {
      newErrors.roleName = "Role name is required";
    }

    // Optionally: Ensure at least one permission is selected
    const hasPermission = Object.values(rolePermissions).some(
      (perms) => perms.view || perms.add || perms.edit || perms.delete
    );
    if (!hasPermission) {
      newErrors.permissions = "At least one permission should be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRoleNameChange = (e) => {
    setRoleName(e.target.value);
  };

  const handlePermissionChange = (section, permission) => {
    setRolePermissions((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [permission]: !prev[section][permission],
      },
    }));
  };

  const isAllSelected = (permissionType) => {
    if (!rolePermissions || Object.keys(rolePermissions).length === 0) return false;
    return Object.values(rolePermissions).every(
      (perms) => perms[permissionType] === true
    );
  };

  const handleSelectAll = (permissionType) => {
    const currentState = isAllSelected(permissionType);
    const newrolePermissions = { ...rolePermissions };
    Object.keys(newrolePermissions).forEach((section) => {
      newrolePermissions[section] = {
        ...newrolePermissions[section],
        [permissionType]: !currentState,
      };
    });
    setRolePermissions(newrolePermissions);
  };

  const handleCancel = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate("/user-role-permission");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setTimeout(() => {
        const errorElement = document.querySelector(".is-invalid, div.text-danger");
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          const inputToFocus = errorElement.tagName === "INPUT" || errorElement.tagName === "SELECT"
            ? errorElement
            : errorElement.parentElement?.querySelector("input, select, textarea");
          if (inputToFocus) inputToFocus.focus();
        }
      }, 100);
      return;
    }

    setIsLoading(true);

    try {
      // Convert the rolePermissions object to an array

      const roleData = {
        roleName,
        rolePermissions: Object.entries(rolePermissions).map(
          ([key, perms]) => ({
            feature: key,
            ...perms,
          })
        ),
        ...(isEditMode && { id: editRole._id }),
      };

      console.log('Submitting roleData:', roleData);
      const response = isEditMode
        ? await rolesApiProvider.updateRole(editRole._id, roleData)
        : await rolesApiProvider.createRole(roleData);

      console.log('Role API response:', response);
      if (response && response.status) {
        // API provider now returns { status: true, response: <data> }
        // Success notification is handled by BaseApiService

        if (isModal && onClose) {
          onClose();
        } else {
          navigate("/user-role-permission");
        }
      }
    } catch (error) {
      console.error("Submission failed:", error);
      // Error notification is handled by BaseApiService
    } finally {
      setIsLoading(false);
    }
  };

  const content = (
    <div className={isModal ? "" : "card h-100 p-0 radius-12"}>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit}>
          <div className="row gy-3 mb-4">
            <div className="col-sm-6">
              <label className="form-label">Role Name <span className="text-danger">*</span></label>
              <input
                id="roleName"
                name="roleName"
                type="text"
                className="form-control"
                placeholder="Enter role name"
                value={roleName}
                onChange={handleRoleNameChange}
                required
              />
              {errors.roleName && (
                <div className="text-danger mt-1">{errors.roleName}</div>
              )}
            </div>
          </div>


          <div className="row">
            <div class="col-xxl-12">
              <div className="mb-3">
                <h5 className="card-title mb-0">Role Permissions</h5>
              </div>
              {errors.permissions && (
                <div className="text-danger mb-2">{errors.permissions}</div>
              )}

              <div class="panel-body">
                <div>
                  <table class="table table-striped table-centered align-middle table-nowrap mb-0 table-check create-role-table">
                    <thead>
                      <tr>
                        <th className="th-title">Feature</th>
                        <th className="text-center">
                          <div class="checkbox-replace">
                            <label class="i-checks align-checkbox">
                              <input
                                type="checkbox"
                                id="all_view"
                                checked={isAllSelected("view")}
                                onChange={() => handleSelectAll("view")}
                              />
                              View
                            </label>
                          </div>
                        </th>
                        <th className="text-center">
                          <div class="checkbox-replace">
                            <label class="i-checks align-checkbox">
                              <input
                                type="checkbox"
                                id="all_add"
                                checked={isAllSelected("add")}
                                onChange={() => handleSelectAll("add")}
                              />
                              Add
                            </label>
                          </div>
                        </th>
                        <th className="text-center">
                          <div class="checkbox-replace">
                            <label class="i-checks align-checkbox">
                              <input
                                type="checkbox"
                                id="all_edit"
                                checked={isAllSelected("edit")}
                                onChange={() => handleSelectAll("edit")}
                              />
                              Edit
                            </label>
                          </div>
                        </th>
                        <th className="text-center">
                          <div class="checkbox-replace">
                            <label class="i-checks align-checkbox">
                              <input
                                type="checkbox"
                                id="all_delete"
                                checked={isAllSelected("delete")}
                                onChange={() => handleSelectAll("delete")}
                              />
                              Delete
                            </label>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Dashboard */}
                      <tr>
                        <th className="th-title" colSpan="5">
                          Dashboard
                        </th>
                      </tr>
                      <tr>
                        <td>Dashboard</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`dashboard-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.dashboard[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "dashboard",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>

                      {/* Website */}
                      <tr>
                        <th className="th-title" colSpan="5">
                          Website
                        </th>
                      </tr>
                      <tr>
                        <td>Home Banner</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`homePage-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.homePage[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "homePage",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>

                      {/* Catalog */}
                      <tr>
                        <th className="th-title" colSpan="5">
                          Catalog
                        </th>
                      </tr>
                      <tr>
                        <td>Categories</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`categories-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.categories[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "categories",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Attributes</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`attributes-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.attributes[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "attributes",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Brands</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`brands-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.brands[permission]}
                                onChange={() =>
                                  handlePermissionChange("brands", permission)
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Products</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`products-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.products[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "products",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Tax</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`taxList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.taxList[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "taxList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      {/* <tr>
                        <td>Offers</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`offerCreations-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.offerCreations[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "offerCreations",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Route Creation</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`rootCreation-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.rootCreation[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "rootCreation",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Coupon code</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`couponCreation-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.couponCreation[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "couponCreation",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Orders */}
                      <tr>
                        <th className="th-title" colSpan="5">
                          Orders
                        </th>
                      </tr>
                      {/* <tr>
                        <td>Wholesale Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`orderList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.orderList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "orderList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Retailer Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`retailerOrderList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.retailerOrderList[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "retailerOrderList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}
                      <tr>
                        <td>Customer Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`customerOrder-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.customerOrder[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "customerOrder",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      {/* <tr>
                        <td>POS Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`posOrder-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.posOrder[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "posOrder",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}
                      <tr>
                        <td>Return Request</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`returnRequest-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.returnRequest[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "returnRequest",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Cancelled Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`cancelledOrder-${permission}`}>
                              <input
                                type="checkbox"
                                checked={rolePermissions.cancelledOrder[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "cancelledOrder",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      {/* <tr>
                        <td>Order Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`orderReport-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.orderReport[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "orderReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Users */}
                      <tr>
                        <th className="th-title" colSpan="5">
                          Users
                        </th>
                      </tr>
                      <tr>
                        <td>User List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`usersList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.usersList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "usersList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>User Role & Permission</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`userRolePermission-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.userRolePermission[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "userRolePermission",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Customer List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`customerList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.customerList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "customerList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Contact Requests</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`contactRequests-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.contactRequests[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "contactRequests",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      {/* <tr>
                        <td>Password Management</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`passwordManagement-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.passwordManagement[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "passwordManagement",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>User Activity Logs</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`userActivityLog-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.userActivityLog[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "userActivityLog",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Users Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`usersReport-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.usersReport[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "usersReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* POS */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          POS
                        </th>
                      </tr>
                      <tr>
                        <td>POS</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`posNew-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.posNew[permission]}
                                onChange={() =>
                                  handlePermissionChange("posNew", permission)
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>POS Order History</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`posOrderHistroy-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.posOrderHistroy[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "posOrderHistroy",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Customer */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Customer
                        </th>
                      </tr>
                      
                      <tr>
                        <td>Customer Order History</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`customerOrderHistroy-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.customerOrderHistroy[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "customerOrderHistroy",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Customer Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`customerReports-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.customerReports[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "customerReports",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Wholesalers */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Wholesalers
                        </th>
                      </tr>
                      <tr>
                        <td>Wholesaler List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`wholesalerList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.wholesalerList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "wholesalerList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Wholesaler Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`wholesalerOrderHistory-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.wholesalerOrderHistory[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "wholesalerOrderHistory",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Credit Management</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center"
                              key={`wholesalerCreditManagement-${permission}`}
                            >
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.wholesalerCreditManagement[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "wholesalerCreditManagement",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Payment & Dues</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`wholesalerPaymentDues-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.wholesalerPaymentDues[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "wholesalerPaymentDues",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Wholesaler Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`wholesalerReports-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.wholesalerReports[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "wholesalerReports",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Retailers */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Retailers
                        </th>
                      </tr>
                      <tr>
                        <td>Retailers List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`retailersList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.retailersList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "retailersList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Retailer Orders</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`retailerOrderHistory-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.retailerOrderHistory[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "retailerOrderHistory",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Credit Management</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center"
                              key={`retailerCreditManagement-${permission}`}
                            >
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.retailerCreditManagement[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "retailerCreditManagement",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Payment & Dues</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`retailerPaymentDues-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.retailerPaymentDues[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "retailerPaymentDues",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Retailer Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`retailerReports-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.retailerReports[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "retailerReports",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Vendors */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Vendors
                        </th>
                      </tr> */}
                      {/* <tr>
                        <td>Vendor List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`vendorList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.vendorList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "vendorList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Vendor Purchase List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`vendorOrderList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.vendorOrderList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "vendorOrderList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Vendor Payments</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`vendorPayments-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.vendorPayments[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "vendorPayments",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Payment & Dues</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`vendorPaymentDues-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.vendorPaymentDues[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "vendorPaymentDues",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Vendor Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`vendorReports-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.vendorReports[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "vendorReports",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Salesman */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Salesman
                        </th>
                      </tr>
                      <tr>
                        <td>Salesman List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`salesmanList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.salesmanList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "salesmanList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Targets & Incentives</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center"
                              key={`salesmanTargetsIncentives-${permission}`}
                            >
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.salesmanTargetsIncentives[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "salesmanTargetsIncentives",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Sales Cash Settlement</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`salesCashSettlement-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.salesCashSettlement[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "salesCashSettlement",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Salesman Performance</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`salesmanPerformance-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.salesmanPerformance[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "salesmanPerformance",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Salesman Order History</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`salesmanOrderHistory-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.salesmanOrderHistory[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "salesmanOrderHistory",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Salesman Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`salesmanReport-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.salesmanReport[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "salesmanReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Delivery */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Delivery
                        </th>
                      </tr>
                      <tr>
                        <td>Delivery List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`deliveryList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Delivery Tracking Updates</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`deliveryTrackingUpdates-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryTrackingUpdates[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryTrackingUpdates",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Delivery Person List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`deliveryPersonList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryPersonList[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryPersonList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Delivery Performance</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center"
                              key={`deliveryPerformancePayroll-${permission}`}
                            >
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryPerformancePayroll[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryPerformancePayroll",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Delivery Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`deliveryReports-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryReports[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryReports",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Delivery Man Request</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`deliveryManRequest-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryManRequest[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryManRequest",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Delivery Man Complaint</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`deliveryManComplaint-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.deliveryManComplaint[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "deliveryManComplaint",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Payment & Credit */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Payment & Credit
                        </th>
                      </tr>
                      <tr>
                        <td>Payment Transaction List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`paymentTransactionList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.paymentTransactionList[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "paymentTransactionList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Record Manual Payment</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`recordManualPayment-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.recordManualPayment[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "recordManualPayment",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Outstanding Payments</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`outstandingPayment-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.outstandingPayment[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "outstandingPayment",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Process Refund</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`processRefund-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.processRefund[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "processRefund",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Payments & Credit Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`paymentCreditReport-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.paymentCreditReport[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "paymentCreditReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Inventory */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Inventory
                        </th>
                      </tr>
                      <tr>
                        <td>Add stock</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`addStock-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.addStock[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "addStock",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Inventory List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`inventoryList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.inventoryList[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "inventoryList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Inventory Log</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`inventoryLog-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.inventoryLog[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "inventoryLog",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Inventory Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`inventoryReports-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.inventoryReports[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "inventoryReports",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Petty Cash */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Petty Cash
                        </th>
                      </tr>
                      <tr>
                        <td>Petty Cash Transaction List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center"
                              key={`pettycashTransactionList-${permission}`}
                            >
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.pettycashTransactionList[
                                  permission
                                  ]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "pettycashTransactionList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Petty Cash Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`pettyCashReport-${permission}`}>
                              <input
                                type="checkbox"

                                checked={
                                  rolePermissions.pettyCashReport[permission]
                                }
                                onChange={() =>
                                  handlePermissionChange(
                                    "pettyCashReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}

                      {/* Tax */}
                      {/* <tr>
                        <th className="th-title" colSpan="5">
                          Tax
                        </th>
                      </tr>
                      <tr>
                        <td>Tax List</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`taxList-${permission}`}>
                              <input
                                type="checkbox"

                                checked={rolePermissions.taxList[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "taxList",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr> */}
                      {/* Reports */}
                      <tr>
                        <th className="th-title" colSpan="5">
                          Reports
                        </th>
                      </tr>
                      <tr>
                        <td>Product Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`productReport-${permission}`}>
                              <input
                                type="checkbox"
                                checked={rolePermissions.productReport[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "productReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>Stock Reports</td>
                        {["view", "add", "edit", "delete"].map(
                          (permission) => (
                            <td className="text-center" key={`stockReport-${permission}`}>
                              <input
                                type="checkbox"
                                checked={rolePermissions.stockReport[permission]}
                                onChange={() =>
                                  handlePermissionChange(
                                    "stockReport",
                                    permission
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="row" style={{ marginTop: "20px" }}>
            <div className="col text-end">

              {((isEditMode && canEdit) || (!isEditMode && canAdd)) && (
                <button
                  type="submit"
                  className="btn btn-primary me-2 px-32"
                >
                  {isEditMode ? "Update" : "Save"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary waves-effect px-32"
                onClick={handleCancel}
              >
                Cancel
              </button>

            </div>
          </div>
        </form>
      </div>
    </div>
  );

  if (isModal) {
    return <div className="create-role-modal-content">{content}</div>;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">{content}</div>
    </div>
  );
}

